import {Component, OnDestroy, OnInit} from '@angular/core';
import {BmPaymentPlanService} from '../../service/bm-payment-plan.service';
import {BalanceEntry} from '../../model/balance-entry.interface';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';


@Component({
    selector: 'bm-payment-plan-balance',
    templateUrl: './bm-payment-plan-balance.component.html',
    styleUrls: ['./bm-payment-plan-balance.component.scss'],
    standalone: false
})
export class BmPaymentPlanBalanceComponent implements OnInit, OnDestroy {


    balanceEntries: BalanceEntry[] = [];
    totalBalance: number = 0;

    private destroy$ = new Subject<void>();

    // One-time payment form
    showOneTimePaymentDialog: boolean = false;

    constructor(private paymentPlanService: BmPaymentPlanService) {
    }

    ngOnInit(): void {
        this.subscribeToBalanceChanges();
    }

    /**
     * Subscribe to balance entries changes
     */
    private subscribeToBalanceChanges(): void {
        this.paymentPlanService.balanceEntries$
            .pipe(takeUntil(this.destroy$))
            .subscribe(entries => {
                this.balanceEntries = entries;
                this.calculateTotalBalance();
            });

    }

    /**
     * Calculate total balance from all entries
     */
    private calculateTotalBalance(): void {
        this.totalBalance = this.balanceEntries.reduce(
            (sum, entry) => sum + entry.amount,
            0
        );
    }

    /**
     * Remove a balance entry
     */
    async removeEntry(entry: BalanceEntry): Promise<void> {
        if (!entry.removable) {
            return;
        }

        // Determine which service method to call based on entry type
        if (entry.type === 'one-time-payment') {
            await this.paymentPlanService.removeChargeEntry(entry.id);
            // await this.paymentPlanService.removeOneTimePayment(entry.name);
        } else if (entry.type === 'contract-dunning' || entry.type === 'contract-reactivation') {
            // Extract contract identifier from entry id
            const contractIdentifier = entry.id.replace('contract-', '');
            await this.paymentPlanService.removeContractActivity(contractIdentifier);
        } else if (entry.type === 'charge') {
            // For charges, we need to get the original charge and restore it
            const dunningData = this.paymentPlanService.getDunningData();
            // await this.paymentPlanService.removeChargeEntry(entry.id);
            if (dunningData) {
                const charge = dunningData.charges.find(c => c.name === entry.name);
                if (charge) {
                    await this.paymentPlanService.adjustCharge(charge, 'remove');
                }
            }
        }
    }

    /**
     * Format entry type for display
     */
    getEntryTypeLabel(type: string): string {
        const labels: { [key: string]: string } = {
            'charge': 'Charge',
            'contract-dunning': 'Contract (Dunning)',
            'contract-reactivation': 'Contract (Reactivation)',
            'charge-removal': 'Charge Removed',
            'charge-reduction': 'Charge Reduced',
            'one-time-payment': 'One-Time Payment'
        };
        return labels[type] || type;
    }

    /**
     * Get CSS class for entry type
     */
    getEntryClass(entry: BalanceEntry): string {
        if (entry.amount < 0) {
            return 'entry-negative';
        } else if (entry.type === 'contract-reactivation') {
            return 'entry-reactivation';
        } else if (entry.type === 'contract-dunning') {
            return 'entry-dunning';
        } else {
            return 'entry-positive';
        }
    }


    /**
     * Show one-time payment dialog
     */
    openOneTimePaymentDialog(): void {
        this.showOneTimePaymentDialog = true;
    }

    hasContractEntries(): boolean {
        return this.balanceEntries.some(
            item =>
                item.type === 'contract-dunning' ||
                item.type === 'contract-reactivation'
        );
    }

    hasOneTimePayments(): boolean {
        return this.balanceEntries.some(
            item => item.type === 'one-time-payment'
        );
    }


    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
